import React from 'react';
import PropTypes from 'prop-types';

import settingsReader from '../../store/readHelpers/SettingsReader';
import responseReader from '../../store/readHelpers/ResponseReader';
import questionReader from '../../store/readHelpers/QuestionReader';
import { t } from '../../services/i18nService';
import utilService from '../../services/UtilService';
import aclService from '../../services/AclService';

import styles from '../tableOfContents/tableOfContents.scss';

class ResultSection extends React.Component {
    static propTypes = {
        questions: PropTypes.array.isRequired,
        result: PropTypes.object,
        section: PropTypes.object,
        settings: PropTypes.object.isRequired,
        sectionIndex: PropTypes.number,
        ui: PropTypes.object,
    };

    getQuestionRows = () => {
        const { questions, settings, result } = this.props;
        const showTitles = settingsReader.isShowQuestionTitlesEnabled(settings);
        const showResults = aclService.canSeeScore();

        return questions.map((question) => {
            const { contentRevisionId, objectType } = question;
            const isManuallyScored = utilService.isQuestionTypeManuallyScored(question.objectType);
            const isNotScorable = utilService.isQuestionTypeNotScorable(question.objectType);
            const questionTypeName = t(`questionTypes.${objectType}`);
            const questionResult = result.questions ? result.questions[contentRevisionId] : undefined;
            const isDocument = questionReader.isDocument(question);
            const isContentBlock = questionReader.isContentBlock(question);
            const isForm = questionReader.isForm(question);
            const isAttemptedQuestion = responseReader.isQuestionAttempted(question);
            const className = isAttemptedQuestion ? 'number attempted' : 'number';
            let questionNumberingElement;
            let questionWithIcon;
            let feedbackName = '';
            let scoreStr = '';

            if (questionResult && questionResult.autoFeedback) {
                const { autoFeedback, manualScore, score } = questionResult;
                const maxScoreStr = `${question.maxScore}`;
                const scoreToUse = manualScore && manualScore.length === 1 ? manualScore[0] : score;
                const scoreInt = parseInt(scoreToUse, 10);

                feedbackName = t(`result.${autoFeedback}`);
                if (isNotScorable) {
                    scoreStr = '';
                } else if (isManuallyScored && manualScore.length === 0) {
                    scoreStr = t('result.manuallyMarked');
                } else if (maxScoreStr.indexOf('.') !== -1) {
                    const scoreNum = scoreInt === scoreToUse ? scoreInt : scoreToUse;
                    scoreStr = `${scoreNum}/${question.maxScore}`;
                } else {
                    scoreStr = `${scoreToUse}/${question.maxScore}`;
                }
            }

            if (isDocument) {
                questionWithIcon = 'fa-info';
            } else if (isForm) {
                questionWithIcon = 'fa-check-square-o';
            } else if (isContentBlock) {
                questionWithIcon = 'fa-file-text-o';
            }

            if (questionWithIcon) {
                questionNumberingElement = <i className={`fa ${questionWithIcon}`} aria-hidden="true" />;
            } else if (isForm || isDocument) {
                questionNumberingElement = '';
            } else if (isAttemptedQuestion) {
                questionNumberingElement = (
                    <React.Fragment>
                        <span className="sr-only">
                            {t('generic.attempted')}
                            {t('generic.questionNumberPrefix')}
                        </span>
                        {question.number}
                    </React.Fragment>
                );
            } else {
                questionNumberingElement = (
                    <React.Fragment>
                        <span className="sr-only">
                            {t('generic.unattempted')}
                            {t('generic.questionNumberPrefix')}
                        </span>
                        {question.number}
                    </React.Fragment>
                );
            }

            return (
                <tr key={question.contentRevisionId}>
                    <td aria-label={t('table.headerNumber')} className={`${className} ${showTitles ? '' : 'number-only'}`}>
                        { questionNumberingElement }
                    </td>
                    {
                        showTitles ?
                            <td aria-label={t('table.headerQuestionTitle')}>
                                {showTitles ? question.title : null}
                            </td>
                            :
                            null
                    }
                    {
                        showResults ?
                            <td aria-label={t('table.headerStatus')}>
                                {feedbackName}
                            </td>
                            :
                            null
                    }
                    {
                        showResults ?
                            <td aria-label={t('table.headerMarks')}>
                                {scoreStr}
                            </td>
                            :
                            null
                    }
                    <td className={styles.objectType} aria-label={t('table.headerQuestionType')}>
                        {questionTypeName}
                    </td>
                </tr>
            );
        });
    };

    getHeaderRow = () => {
        const { settings } = this.props;
        const showTitles = settingsReader.isShowQuestionTitlesEnabled(settings);
        const showResults = aclService.canSeeScore();

        return (
            <tr key="-1">
                <th scope="col">{t('table.headerQuestionNo')}</th>
                {
                    showTitles ?
                        <th>{t('table.headerQuestionTitle')}</th>
                        :
                        null
                }
                {
                    showResults ?
                        <th>
                            {t('table.headerStatus')}
                        </th>
                        :
                        null
                }
                {
                    showResults ?
                        <th>
                            {t('table.headerMarks')}
                        </th>
                        :
                        null
                }
                <th className={styles.objectType}>
                    {t('table.headerQuestionType')}
                </th>
            </tr>
        );
    };

    render() {
        const { section, sectionIndex, ui } = this.props;
        const sectionId = `section-${section.id}`;
        const showSectionTitle = settingsReader.isShowSectionEnabled();
        const tableHeaderOnEachTBody = ui.variables.toc__tableHeaderOnEachTBody && showSectionTitle;
        const questionRows = tableHeaderOnEachTBody
            ? [this.getHeaderRow()].concat(this.getQuestionRows())
            : this.getQuestionRows();

        return (
            <tbody>
                { !tableHeaderOnEachTBody && sectionIndex === 0 ?
                    this.getHeaderRow()
                    : null
                }
                {
                    showSectionTitle ?
                        <tr>
                            <td colSpan="10" className={styles.sectionTitle} id={sectionId}>
                                <span className="sr-only">{t('generic.sectionNumberPrefix')}</span>
                                <span className={styles.sectionName}>{section.title}</span>
                            </td>
                        </tr>
                        : null
                }
                {
                    questionRows
                }
                <tr className={styles.spacer}>
                    <td colSpan="10" />
                </tr>
            </tbody>
        );
    }
}

export default ResultSection;
